import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box, Heading, Hide, Flex } from 'roo-ui/components';
import ImageGallery from 'components/ImageGallery';
import Offer from 'pages/Property/PropertyAvailability/RoomTypes/RoomTypeList/Offer';
import { useDataLayer } from 'hooks/useDataLayer';
import theme from 'lib/theme';
import RoomTypeDisplayMeta from './components/RoomTypeDisplayMeta';
import { getIsLoading } from 'store/propertyAvailability/propertyAvailabilitySelectors';
import UnavailableRoomType from 'pages/Property/PropertyAvailability/RoomTypes/RoomTypeList/UnavailableRoomType';
import RoomOfferDetails from 'pages/Property/PropertyAvailability/RoomTypes/RoomTypeList/Offer/components/RoomOfferDetails';
import {
  CurrentImageText,
  LeftNav,
  RightNav,
  RoomTypeWrapper,
  UnavailableMessage,
  FullWidthHeader,
  OffersWrapper,
} from './components/primitives';

const OffersList = ({ roomName, offers, roomTypeId }) => {
  const isLoading = useSelector(getIsLoading);
  return (
    <OffersWrapper>
      {offers.map((offer, counter) => (
        <Offer
          roomTypeId={roomTypeId}
          offer={offer}
          counter={counter + 1}
          key={offer.id}
          roomName={roomName}
          isLoading={isLoading}
          mb={counter < offers.length - 1 ? [0, 4] : 0}
        />
      ))}
    </OffersWrapper>
  );
};
OffersList.propTypes = {
  roomName: PropTypes.string.isRequired,
  offers: PropTypes.array.isRequired,
  roomTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const RoomType = ({ roomType, hasQuery }) => {
  const { emitInteractionEvent } = useDataLayer();
  const { name, offers, maxOccupantCount, id } = roomType;
  const roomImages = roomType?.images || [];
  const hasImages = roomImages.length > 0;
  const hasOffers = !!(offers && offers.length);
  const thumbnailSize = theme.imageSizes.roomTypeThumbnail;
  const isUnavailable = !hasOffers || hasOffers === undefined ? true : false;

  const handleOpenGallery = useCallback(() => {
    emitInteractionEvent({ type: 'Room Type Gallery', value: 'Gallery Opened' });
  }, [emitInteractionEvent]);

  const handleGalleryImageChanged = useCallback(
    (id) => {
      emitInteractionEvent({ type: 'Room Type Gallery', value: `Gallery Scrolled to ${id}` });
    },
    [emitInteractionEvent],
  );

  if (isUnavailable) {
    return <UnavailableRoomType roomType={roomType} hasQuery={hasQuery} />;
  }

  return (
    <RoomTypeWrapper>
      <FullWidthHeader xs sm>
        <Heading.h3 fontSize="lg" mb={4} fontWeight="normal">
          {name}
        </Heading.h3>
      </FullWidthHeader>
      <Flex flexDirection={['column', 'row', 'column']} pb={[5, 0]} borderBottom={[1, 0]} borderColor="greys.dusty">
        {!hasImages && (
          <Flex
            height={thumbnailSize.height}
            width={thumbnailSize.width}
            borderRadius="default"
            bg="greys.alto"
            alignItems="center"
            justifyContent="center"
            color="greys.steel"
          >
            Image not available
          </Flex>
        )}
        {hasImages && (
          <Box position="relative" height={thumbnailSize.height} width={thumbnailSize.width} mb={4}>
            <ImageGallery
              images={roomImages}
              onClick={handleOpenGallery}
              onImageChanged={handleGalleryImageChanged}
              imageHeight={thumbnailSize.height}
              imageWidth={thumbnailSize.width}
              hideThumbnails
              imageDescriptionRenderer={CurrentImageText}
              leftNavRenderer={LeftNav}
              rightNavRenderer={RightNav}
              imageSize="medium"
            />
          </Box>
        )}
        <Flex flex="1 1 auto" justifyContent="space-between" px={[4, 6, 0]} py={[0, 6, 0]}>
          <Box>
            <Hide md lg>
              <Heading.h3 fontSize="lg" fontWeight="normal">
                {name}
              </Heading.h3>
            </Hide>
            <RoomTypeDisplayMeta maxOccupantCount={maxOccupantCount} />
            <RoomOfferDetails roomType={roomType} />
          </Box>
          {!hasQuery && <UnavailableMessage>Select stay dates to show prices</UnavailableMessage>}
          {hasQuery && !hasOffers && <UnavailableMessage>Unavailable</UnavailableMessage>}
        </Flex>
      </Flex>

      {hasOffers && <OffersList roomName={name} offers={offers} roomTypeId={id} />}
    </RoomTypeWrapper>
  );
};

RoomType.propTypes = {
  roomType: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    roomTypeFacilities: PropTypes.array.isRequired,
    offers: PropTypes.array,
    maxOccupantCount: PropTypes.number,
    images: PropTypes.array,
    mainImage: PropTypes.shape({
      urlMedium: PropTypes.string,
      caption: PropTypes.string,
    }),
  }).isRequired,
  hasQuery: PropTypes.bool,
};

RoomType.defaultProps = {
  hasQuery: false,
};

export default memo(RoomType);
